"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { LandingNav, LandingFooter } from "@/components/landing";

const aiPrivacyCards = [
  {
    icon: "block",
    title: "No Model Training",
    description:
      "We never use your data to train, fine-tune, or enhance any AI model. Our AI runs on enterprise-grade infrastructure governed by data processing agreements that explicitly bar the use of customer data for model development.",
  },
  {
    icon: "shield_person",
    title: "Full Data Isolation",
    description:
      "Strict tenant separation across every client. Your firm's data is completely segregated — no sharing between firms, ever.",
  },
  {
    icon: "visibility",
    title: "Traceable AI",
    description:
      "Every agent action is logged and auditable. Our AI shows its sources, changes, and rationale at every step.",
  },
];

const dataProtectionCards = [
  {
    icon: "encrypted",
    title: "Encrypted in Transit",
    description:
      "TLS 1.3 on every connection. Your documents are protected from the moment they leave your browser.",
  },
  {
    icon: "key",
    title: "Encrypted at Rest",
    description:
      "All stored data is protected with AES-256 encryption. Keys are rotated automatically through managed infrastructure.",
  },
  {
    icon: "passkey",
    title: "No Credential Storage",
    description:
      "We never store your passwords. Access is managed through revocable tokens only.",
  },
  {
    icon: "public",
    title: "US-Only Hosting",
    description:
      "All customer data is stored and processed in the United States.",
  },
  {
    icon: "admin_panel_settings",
    title: "Access Controls",
    description:
      "All access requests go through identity verification and are recorded in an immutable audit trail.",
  },
  {
    icon: "lock",
    title: "Multi-Factor Authentication",
    description:
      "We provide multi-factor authentication options to secure every account. Role-based access controls for your entire team.",
  },
];

const dataOwnershipItems = [
  {
    icon: "download",
    title: "Export Anytime",
    description: "Download all your data at any point.",
  },
  {
    icon: "delete",
    title: "Delete on Request",
    description:
      "Request deletion and all your data is permanently removed.",
  },
  {
    icon: "lock_reset",
    title: "No Selling or Sharing",
    description:
      "We don't monetize your data. It is never sold to or shared with any third party.",
  },
];

export default function SecurityPage() {
  return (
    <div className="flex flex-col min-h-screen bg-landing-bg text-landing-text">
      <LandingNav />
      <main>
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-24 sm:pt-32 pb-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-7xl font-serif font-bold text-landing-text leading-tight mb-8">
              Security isn&apos;t a feature. It&apos;s the foundation.
            </h1>
            <p className="text-xl md:text-2xl text-landing-text-secondary leading-relaxed">
              Your clients trust you with their most sensitive financial
              information. We built Dezu so you never have to think twice
              about where that data goes.
            </p>
          </motion.div>
        </section>

        {/* AI Privacy */}
        <section className="py-16 sm:py-20 px-4 sm:px-8 bg-landing-surface/10">
          <div className="max-w-7xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-3xl md:text-4xl font-serif font-bold text-landing-text mb-12"
            >
              AI built around your privacy.
            </motion.h2>
            <div className="grid md:grid-cols-3 gap-8">
              {aiPrivacyCards.map((card, i) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                  className="bg-white p-8 rounded-3xl border border-landing-surface/20 shadow-sm hover:-translate-y-1 transition-transform duration-300"
                >
                  <div className="w-12 h-12 bg-landing-primary/10 rounded-xl flex items-center justify-center mb-6">
                    <span className="material-symbols-outlined text-landing-primary text-3xl">
                      {card.icon}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-landing-text mb-4">
                    {card.title}
                  </h3>
                  <p className="text-landing-text-secondary leading-relaxed">
                    {card.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Secure by Design */}
        <section className="bg-landing-text py-20 sm:py-24 px-4 sm:px-8 overflow-hidden relative">
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-4xl md:text-5xl font-serif font-bold text-white mb-12"
            >
              Secure by design.
            </motion.h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dataProtectionCards.map((card, i) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.6 }}
                  className="bg-white/5 border border-white/10 backdrop-blur-md p-8 rounded-3xl hover:bg-white/10 transition-colors duration-300"
                >
                  <span className="material-symbols-outlined text-landing-primary text-4xl mb-6 block">
                    {card.icon}
                  </span>
                  <h3 className="text-white font-bold text-lg mb-3">
                    {card.title}
                  </h3>
                  <p className="text-landing-surface text-sm leading-relaxed">
                    {card.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
          {/* Decorative blur */}
          <div aria-hidden="true" className="absolute -top-1/4 -right-1/4 w-1/2 h-full bg-landing-primary opacity-10 blur-[120px] rounded-full" />
        </section>

        {/* Your Data, Your Control */}
        <section className="bg-landing-bg py-20 sm:py-24 px-4 sm:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-3xl md:text-4xl font-serif font-bold text-landing-text mb-12"
            >
              You own your data. Full stop.
            </motion.h2>
            <div className="space-y-12">
              {dataOwnershipItems.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                  className="flex items-start gap-4 sm:gap-8 group"
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-landing-surface/20 rounded-2xl flex items-center justify-center group-hover:bg-landing-primary/10 transition-colors">
                    <span className="material-symbols-outlined text-landing-primary text-3xl">
                      {item.icon}
                    </span>
                  </div>
                  <div className="pt-2">
                    <h4 className="text-xl font-bold text-landing-text mb-2">
                      {item.title}
                    </h4>
                    <p className="text-landing-text-secondary text-lg leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-landing-cream py-20 sm:py-24 px-4 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-landing-text mb-6">
              Ready to see it in action?
            </h2>
            <p className="text-landing-text-secondary text-lg mb-10">
              Book a 15-minute demo and see how Dezu handles your firm&apos;s documents.
            </p>
            <Link
              href="https://cal.com/riyadev/15min"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-landing-primary text-white px-12 py-5 rounded-full font-bold text-lg hover:bg-landing-primary-hover transition-all hover:scale-105 shadow-xl"
            >
              Book a Demo
            </Link>
          </motion.div>
        </section>
      </main>
      <LandingFooter />
    </div>
  );
}
